import React, { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import { PageHeader, EmptyState, Section } from "@/components/Shared";
import api from "@/lib/api";
import { TL, CARI_TYPE_LABEL } from "@/lib/format";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  PieChart, Pie, Cell, BarChart, Bar,
} from "recharts";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowUp, ArrowDown, Minus } from "lucide-react";

const CARI_COLORS = {
  musteri: "hsl(var(--chart-1))",
  acente: "hsl(var(--chart-3))",
  tedarikci: "hsl(var(--chart-2))",
};

export default function RaporlarPage() {
  const [monthly, setMonthly] = useState([]);
  const [range, setRange] = useState("6");
  const [cariler, setCariler] = useState([]);
  const [categoryReport, setCategoryReport] = useState({ gelir_kategoriler: [], gider_kategoriler: [] });
  const [comparison, setComparison] = useState({ current_month: "", previous_month: "", gelir: [], gider: [] });

  useEffect(() => {
    (async () => {
      const [m, c, k, cmp] = await Promise.all([
        api.get(`/dashboard/monthly-flow?months=${range}`),
        api.get(`/cariler`),
        api.get(`/dashboard/kategori-rapor`),
        api.get(`/dashboard/kategori-karsilastirma`),
      ]);
      setMonthly(m.data);
      setCariler(c.data);
      setCategoryReport(k.data);
      setComparison(cmp.data);
    })().catch(console.error);
  }, [range]);

  const byType = ["musteri", "acente", "tedarikci"].map((t) => {
    const list = cariler.filter((c) => c.type === t);
    const receivable = list.reduce((sum, c) => sum + Math.max(0, c.balance), 0);
    const payable = list.reduce((sum, c) => sum + Math.max(0, -c.balance), 0);
    return { type: t, label: CARI_TYPE_LABEL[t], count: list.length, receivable, payable };
  });

  const pieData = byType.filter((x) => x.count > 0).map((x) => ({ name: x.label, value: x.count, type: x.type }));

  const hasFlow = monthly.some((m) => m.gelir !== 0 || m.gider !== 0);
  const hasCari = cariler.length > 0;

  return (
    <Layout>
      <PageHeader
        title="Raporlar"
        subtitle="Nakit akışı ve cari hesap analizi"
        action={
          <Select value={range} onValueChange={setRange}>
            <SelectTrigger className="w-40" data-testid="range-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="3">Son 3 Ay</SelectItem>
              <SelectItem value="6">Son 6 Ay</SelectItem>
              <SelectItem value="12">Son 12 Ay</SelectItem>
            </SelectContent>
          </Select>
        }
        testId="raporlar-header"
      />

      <Section title="Aylık Nakit Akışı Trendi">
        <div className="border border-border rounded-md p-6 bg-background">
          {!hasFlow ? (
            <EmptyState title="Grafik için henüz veri yok" testId="empty-flow-chart" />
          ) : (
            <div className="h-80" data-testid="flow-chart">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={monthly}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                  <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} tickFormatter={(v) => `${(v/1000).toFixed(0)}k`} />
                  <Tooltip contentStyle={{ backgroundColor: "hsl(var(--background))", border: "1px solid hsl(var(--border))", borderRadius: 6 }} formatter={(v) => TL(v)} />
                  <Legend />
                  <Line type="monotone" dataKey="gelir" stroke="hsl(var(--chart-1))" strokeWidth={2} name="Gelir" dot={{ r: 4 }} />
                  <Line type="monotone" dataKey="gider" stroke="hsl(var(--chart-2))" strokeWidth={2} name="Gider" dot={{ r: 4 }} />
                  <Line type="monotone" dataKey="net" stroke="hsl(var(--chart-3))" strokeWidth={2} strokeDasharray="4 4" name="Net" dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </Section>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Section title="Cari Tipine Göre Dağılım">
          <div className="border border-border rounded-md p-6 bg-background">
            {!hasCari ? (
              <EmptyState title="Cari hesap yok" testId="empty-pie" />
            ) : (
              <div className="h-72" data-testid="cari-pie">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={pieData} dataKey="value" nameKey="name" outerRadius={100} label>
                      {pieData.map((entry) => <Cell key={entry.type} fill={CARI_COLORS[entry.type]} />)}
                    </Pie>
                    <Tooltip contentStyle={{ backgroundColor: "hsl(var(--background))", border: "1px solid hsl(var(--border))", borderRadius: 6 }} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </Section>

        <Section title="Tip Bazında Alacak / Borç">
          <div className="border border-border rounded-md p-6 bg-background">
            {!hasCari ? (
              <EmptyState title="Cari hesap yok" testId="empty-bar" />
            ) : (
              <div className="h-72" data-testid="cari-bar">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={byType}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="label" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                    <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} tickFormatter={(v) => `${(v/1000).toFixed(0)}k`} />
                    <Tooltip contentStyle={{ backgroundColor: "hsl(var(--background))", border: "1px solid hsl(var(--border))", borderRadius: 6 }} formatter={(v) => TL(v)} />
                    <Legend />
                    <Bar dataKey="receivable" fill="hsl(var(--chart-1))" name="Alacak" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="payable" fill="hsl(var(--chart-2))" name="Borç" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </Section>
      </div>

      <Section title="Kategori Bazlı Gelir & Gider">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="category-report">
          <div className="border border-border rounded-md p-6 bg-background">
            <div className="text-xs uppercase tracking-wider text-foreground/50 mb-4">Gelir Kategorileri</div>
            {categoryReport.gelir_kategoriler.length === 0 ? (
              <div className="text-sm text-foreground/50 text-center py-6" data-testid="empty-gelir-categories">
                Henüz gelir kaydı yok.
              </div>
            ) : (
              <div className="space-y-2" data-testid="gelir-categories-list">
                {categoryReport.gelir_kategoriler.map((c) => (
                  <div key={c.category} className="flex items-center justify-between py-2 border-b border-border/40 last:border-0">
                    <div>
                      <div className="text-sm font-medium text-foreground">{c.category}</div>
                      <div className="text-[11px] text-foreground/50">{c.count} işlem</div>
                    </div>
                    <div className="text-sm tabular-nums font-medium text-[hsl(var(--success))]">{TL(c.amount)}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="border border-border rounded-md p-6 bg-background">
            <div className="text-xs uppercase tracking-wider text-foreground/50 mb-4">Gider Kategorileri</div>
            {categoryReport.gider_kategoriler.length === 0 ? (
              <div className="text-sm text-foreground/50 text-center py-6" data-testid="empty-gider-categories">
                Henüz gider kaydı yok.
              </div>
            ) : (
              <div className="space-y-2" data-testid="gider-categories-list">
                {categoryReport.gider_kategoriler.map((c) => (
                  <div key={c.category} className="flex items-center justify-between py-2 border-b border-border/40 last:border-0">
                    <div>
                      <div className="text-sm font-medium text-foreground">{c.category}</div>
                      <div className="text-[11px] text-foreground/50">{c.count} işlem</div>
                    </div>
                    <div className="text-sm tabular-nums font-medium text-destructive">{TL(c.amount)}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </Section>

      <Section title={`Aylık Kategori Karşılaştırma (${comparison.previous_month || "—"} → ${comparison.current_month || "—"})`}>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="category-comparison">
          <ComparisonTable
            title="Gelir Kategorileri"
            rows={comparison.gelir}
            positiveIsGood={true}
            testid="comparison-gelir"
            emptyId="comparison-gelir-empty"
          />
          <ComparisonTable
            title="Gider Kategorileri"
            rows={comparison.gider}
            positiveIsGood={false}
            testid="comparison-gider"
            emptyId="comparison-gider-empty"
          />
        </div>
      </Section>
    </Layout>
  );
}

function ComparisonTable({ title, rows, positiveIsGood, testid, emptyId }) {
  return (
    <div className="border border-border rounded-md p-6 bg-background">
      <div className="text-xs uppercase tracking-wider text-foreground/50 mb-4">{title}</div>
      {rows.length === 0 ? (
        <div className="text-sm text-foreground/50 text-center py-6" data-testid={emptyId}>Karşılaştıracak veri yok.</div>
      ) : (
        <div className="space-y-2" data-testid={testid}>
          <div className="grid grid-cols-12 gap-2 text-[10px] uppercase tracking-wider text-foreground/40 border-b border-border pb-2">
            <div className="col-span-5">Kategori</div>
            <div className="col-span-3 text-right">Önceki</div>
            <div className="col-span-3 text-right">Bu Ay</div>
            <div className="col-span-1 text-right">Δ</div>
          </div>
          {rows.map((r) => {
            const isIncrease = r.delta > 0;
            const isDecrease = r.delta < 0;
            const arrow = isIncrease ? "up" : isDecrease ? "down" : "flat";
            // For gelir: increase = good (green), decrease = bad (red)
            // For gider: increase = bad (red), decrease = good (green)
            const goodDirection = positiveIsGood ? isIncrease : isDecrease;
            const badDirection = positiveIsGood ? isDecrease : isIncrease;
            const color = goodDirection ? "text-[hsl(var(--success))]"
                        : badDirection ? "text-destructive"
                        : "text-foreground/50";
            return (
              <div key={r.category} className="grid grid-cols-12 gap-2 items-center py-2 border-b border-border/40 last:border-0">
                <div className="col-span-5 text-sm text-foreground truncate" title={r.category}>{r.category}</div>
                <div className="col-span-3 text-right text-sm tabular-nums text-foreground/60">{TL(r.previous)}</div>
                <div className="col-span-3 text-right text-sm tabular-nums font-medium">{TL(r.current)}</div>
                <div className={`col-span-1 text-right text-xs tabular-nums flex items-center justify-end gap-0.5 ${color}`}>
                  {arrow === "up" && <ArrowUp className="w-3 h-3" />}
                  {arrow === "down" && <ArrowDown className="w-3 h-3" />}
                  {arrow === "flat" && <Minus className="w-3 h-3" />}
                  <span>{r.delta_pct != null ? `${r.delta_pct > 0 ? "+" : ""}${r.delta_pct}%` : "—"}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
